import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { categoryService } from '../../services/category-service';
import type { CategoryDto, CreateCategoryRequest, UpdateCategoryRequest } from '../../types/admin-api';
import { StatusBadge } from '../../components/common/StatusBadge';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { CategoryFormDialog } from './CategoryFormDialog';

const columnHelper = createColumnHelper<CategoryDto>();

function getTranslation(category: CategoryDto, lang: string): string {
  const t = category.translations.find((tr) => tr.languageCode === lang);
  return t?.name || '-';
}

export function CategoryList() {
  const queryClient = useQueryClient();

  const [formDialog, setFormDialog] = useState<{
    isOpen: boolean;
    category: CategoryDto | null;
  }>({ isOpen: false, category: null });

  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    category: CategoryDto | null;
  }>({ isOpen: false, category: null });

  const { data: categories, isLoading, error } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoryService.getAll(),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateCategoryRequest) => categoryService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setFormDialog({ isOpen: false, category: null });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCategoryRequest }) =>
      categoryService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setFormDialog({ isOpen: false, category: null });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoryService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setDeleteDialog({ isOpen: false, category: null });
    },
  });

  const handleFormSubmit = (data: CreateCategoryRequest) => {
    if (formDialog.category) {
      updateMutation.mutate({ id: formDialog.category.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const columns = [
    columnHelper.accessor('slug', {
      header: 'Slug',
      cell: (info) => (
        <span className="px-2 py-1 bg-white/10 text-ink-secondary rounded text-sm font-mono">
          {info.getValue()}
        </span>
      ),
    }),
    columnHelper.display({
      id: 'namePl',
      header: 'Name (PL)',
      cell: (info) => (
        <div>
          <div className="font-medium text-ink-primary">{getTranslation(info.row.original, 'pl')}</div>
          {info.row.original.translations.find((t) => t.languageCode === 'pl')?.description && (
            <div className="text-sm text-ink-muted">
              {info.row.original.translations.find((t) => t.languageCode === 'pl')?.description}
            </div>
          )}
        </div>
      ),
    }),
    columnHelper.display({
      id: 'nameEn',
      header: 'Name (EN)',
      cell: (info) => (
        <div>
          <div className="font-medium text-ink-primary">{getTranslation(info.row.original, 'en')}</div>
          {info.row.original.translations.find((t) => t.languageCode === 'en')?.description && (
            <div className="text-sm text-ink-muted">
              {info.row.original.translations.find((t) => t.languageCode === 'en')?.description}
            </div>
          )}
        </div>
      ),
    }),
    columnHelper.accessor('isActive', {
      header: 'Status',
      cell: (info) => (
        <StatusBadge isEnabled={info.getValue()} activeText="Active" disabledText="Inactive" />
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => setFormDialog({ isOpen: true, category: info.row.original })}
            className="p-1 text-brand-primary-light hover:bg-white/10 rounded"
            title="Edit"
          >
            <Pencil size={18} />
          </button>
          <button
            onClick={() => setDeleteDialog({ isOpen: true, category: info.row.original })}
            className="p-1 text-brand-warm hover:bg-brand-warm/10 rounded"
            title="Delete"
          >
            <Trash2 size={18} />
          </button>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: categories || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (error) {
    return (
      <div className="bg-brand-warm/20 border border-brand-warm/40 text-brand-warm px-4 py-3 rounded">
        Error loading categories: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-xl font-semibold tracking-tight text-ink-primary">Categories</h2>
            <p className="text-sm text-ink-muted mt-1">
              Manage activity categories and their translations
            </p>
          </div>
          <button
            onClick={() => setFormDialog({ isOpen: true, category: null })}
            className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors"
          >
            <Plus size={20} />
            Add Category
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-ink-muted">Loading categories...</div>
        ) : categories && categories.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10">
              <thead className="bg-white/5">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-6 py-3 text-left text-xs font-medium text-ink-secondary uppercase tracking-wider"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-white/10">
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="hover:bg-white/5">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-6 py-4 whitespace-nowrap text-sm">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-ink-muted">No categories found</div>
        )}
      </div>

      {/* Form Dialog */}
      <CategoryFormDialog
        isOpen={formDialog.isOpen}
        category={formDialog.category}
        onClose={() => setFormDialog({ isOpen: false, category: null })}
        onSubmit={handleFormSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
        error={createMutation.error || updateMutation.error}
      />

      {/* Delete Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, category: null })}
        onConfirm={() => deleteDialog.category && deleteMutation.mutate(deleteDialog.category.id)}
        title="Delete Category"
        message={`Are you sure you want to delete "${deleteDialog.category?.slug}"? This will also remove all translations.`}
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
